import { date } from "@planningcenter/datetime-fmt"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import {
  BlankState,
  Card,
  CardGrid,
  LegacyHeading,
  Image,
  Skeleton,
} from "source/shared/components"
import { useQuery } from "@tanstack/react-query"
import qs from "query-string"
import { unlessCommonError } from "source/CommonErrorBoundary"
import { ChannelHero } from "../sermons/shared/ChannelHero"
import { CardProps } from "source/shared/components/Card"

import {
  BaseAPIRecord,
  SingularAPIResponseWithIncluded,
} from "source/shared/types"
import {
  EpisodeAPIRecord,
  HeroAPIRecord,
  HeroEpisodeAPIRecord,
  NoteTemplateAPIRecord,
  ChannelAPIRecord,
} from "../sermons/shared/types"

type BlockAttributes = {
  resource_type: "episodes" | "series"
  display: "most_recent" | "specific_resource"
  headline?: string
  headline_enabled: boolean
  headline_alignment: "left" | "center" | "right"
  layout: "single" | "gallery"
  resource_id?: string
  resource_maximum?: number
  channel_id: string
  featured?: boolean
}

const utmParams = qs.stringify(
  {
    utm_campaign: "pco_content_block",
    utm_medium: "web",
    utm_source: "church_center",
  },
  { arrayFormat: "comma" },
)

const POLL_INTERVAL_IN_MILLISECONDS = 10000

function findIncluded<T extends BaseAPIRecord>(
  included: BaseAPIRecord[],
  type: string,
): T | undefined {
  return included.find((item) => item.type === type) as T
}

function useChannel(id: string) {
  const url = `/publishing/v2/channels/${id}`
  const params = { include: "hero_resource", search: utmParams }
  return useQuery({
    queryFn: () => sessionApiClient.get(url, params),
    queryKey: ["publishing", "channel", id, params],
    refetchInterval: (query) =>
      query.state.data?.data && POLL_INTERVAL_IN_MILLISECONDS,
    retry: unlessCommonError,
  })
}

function useEpisode(id?: string) {
  const url = `/publishing/v2/episodes/${id}`
  const params = {
    include: "channel,note_template",
    "fields[Episode]":
      "title,description,library_art_url,published_to_library_at,church_center_url,note_template,sermon_audio,video_url,library_video_url",
    order: "-published_to_library_at",
    search: utmParams,
  }
  return useQuery({
    queryFn: (): Promise<SingularAPIResponseWithIncluded<EpisodeAPIRecord>> =>
      sessionApiClient.get(url, params),
    queryKey: ["publishing", "episode", id, params],
    retry: unlessCommonError,
  })
}

function useMostRecentEpisode(id: string) {
  const url = `/publishing/v2/channels/${id}/episodes`
  const params = {
    include: "channel,note_template",
    "fields[Episode]":
      "title,description,library_art_url,published_to_library_at,church_center_url,note_template,sermon_audio,video_url,library_video_url",
    order: "-published_to_library_at",
    filter: "published_to_library",
    per_page: 1,
    search: utmParams,
  }
  return useQuery({
    queryFn: (): Promise<SingularAPIResponseWithIncluded<EpisodeAPIRecord[]>> =>
      sessionApiClient.get(url, params),
    queryKey: ["publishing", "channel", id, params],
    select: (data: SingularAPIResponseWithIncluded<EpisodeAPIRecord[]>) => {
      return {
        data: data.data[0],
        included: data.included,
      }
    },
    retry: unlessCommonError,
  })
}

function useSeries({
  channelId,
  seriesId,
  resourceMaximum = 1,
}: {
  channelId: string
  seriesId?: string
  resourceMaximum?: number
}) {
  const url = `/publishing/v2/channels/${channelId}/series${seriesId ? `/${seriesId}` : ""}`

  const params = {
    filter: "published,with_published_episodes",
    search: utmParams,
    per_page: resourceMaximum,
    order: "-ended_at",
  }
  return useQuery({
    queryFn: () => sessionApiClient.get(url, params),
    queryKey: ["publishing", "series", seriesId, params],
    retry: unlessCommonError,
  })
}

export function Sermons({ attributes }: { attributes: BlockAttributes }) {
  const {
    resource_type: resourceType,
    headline_enabled: headlineEnabled,
    featured,
    headline,
    headline_alignment: headlineAlignment,
    layout,
    display,
  } = attributes
  let Component = null
  switch (true) {
    case resourceType === "episodes" && layout === "gallery":
      Component = ChannelView
      break
    case resourceType === "episodes" &&
      layout === "single" &&
      featured === true &&
      display === "most_recent":
      Component = ChannelView
      break
    case resourceType === "episodes" &&
      layout === "single" &&
      featured === false &&
      display === "most_recent":
      Component = MostRecentEpisodeView
      break
    case resourceType === "episodes" &&
      layout === "single" &&
      display === "specific_resource":
      Component = EpisodeView
      break
    case resourceType === "series" && layout === "gallery":
      Component = SeriesGalleryView
      break
    case resourceType === "series" && layout === "single":
      Component = SingleSeriesView
      break
  }

  if (!Component) return null

  return (
    <div style={styles} className="page-block">
      {headlineEnabled && headline && (
        <h2
          style={{
            fontSize: 30,
            textAlign: headlineAlignment,
          }}
        >
          {headline}
        </h2>
      )}

      <Component attributes={attributes} />
    </div>
  )
}

function EpisodeView({ attributes }: { attributes: BlockAttributes }) {
  const { resource_id: resourceId } = attributes

  const { data: apiResponse = {}, isSuccess, isError } = useEpisode(resourceId!)

  const { data: episode, included } = apiResponse as {
    data: EpisodeAPIRecord
    included: BaseAPIRecord[]
  }
  const channel = included?.filter(
    (item) => item.type === "Channel",
  )?.[0] as ChannelAPIRecord
  const noteTemplate = findIncluded<NoteTemplateAPIRecord>(
    included || [],
    "NoteTemplate",
  )

  const showWatchButton =
    channel?.attributes?.enabled_formats.on_demand_video &&
    (!!episode?.attributes?.video_url ||
      !!episode?.attributes?.library_video_url)

  const showNotesButton =
    !!noteTemplate?.attributes?.template &&
    !!channel?.attributes.enabled_formats.sermon_notes

  const showListenButton =
    !(showWatchButton && showNotesButton) &&
    channel?.attributes?.enabled_formats.audio &&
    ["hosted", "remote"].includes(
      episode?.attributes.sermon_audio.attributes.source,
    )

  if (isError) {
    return <ErrorState />
  }

  if (!isSuccess) {
    return <LoadingState />
  }

  if (!included) {
    return <ErrorState />
  }

  if (isSuccess && episode) {
    return (
      <div className="sermon-block-featured-episode d-f fd-c ai-fs g-3">
        <FeaturedItem
          title={episode.attributes.title}
          description={episode.attributes.description}
          imageUrl={episode.attributes.library_art_url || ""}
          dateValue={episode.attributes.published_to_library_at}
          url={episode.attributes.church_center_url}
          showListenButton={showListenButton}
          showNotesButton={showNotesButton}
          showWatchButton={showWatchButton}
        />
      </div>
    )
  }

  return null
}

function MostRecentEpisodeView({
  attributes,
}: {
  attributes: BlockAttributes
}) {
  const { channel_id: channelId } = attributes

  const {
    data: apiResponse = {},
    isSuccess,
    isError,
  } = useMostRecentEpisode(channelId)

  const { data: episode, included } = apiResponse as {
    data: EpisodeAPIRecord
    included: BaseAPIRecord[]
  }
  const channel = included?.filter(
    (item) => item.type === "Channel",
  )?.[0] as ChannelAPIRecord
  const noteTemplate = findIncluded<NoteTemplateAPIRecord>(
    included || [],
    "NoteTemplate",
  )

  const showWatchButton =
    channel?.attributes?.enabled_formats.on_demand_video &&
    (!!episode?.attributes?.video_url ||
      !!episode?.attributes?.library_video_url)

  const showNotesButton =
    !!noteTemplate?.attributes?.template &&
    !!channel?.attributes.enabled_formats.sermon_notes

  const showListenButton =
    !(showWatchButton && showNotesButton) &&
    channel?.attributes?.enabled_formats.audio &&
    ["hosted", "remote"].includes(
      episode?.attributes.sermon_audio.attributes.source,
    )

  if (isError) {
    return <ErrorState />
  }

  if (!isSuccess) {
    return <LoadingState />
  }

  if (!episode || !included) {
    return <ErrorState />
  }

  if (isSuccess && episode) {
    return (
      <div className="sermon-block-featured-episode d-f fd-c ai-fs g-3">
        <FeaturedItem
          title={episode.attributes.title}
          description={episode.attributes.description}
          imageUrl={episode.attributes.library_art_url || ""}
          dateValue={episode.attributes.published_to_library_at}
          url={episode.attributes.church_center_url}
          showListenButton={showListenButton}
          showNotesButton={showNotesButton}
          showWatchButton={showWatchButton}
        />
      </div>
    )
  }

  return null
}

function ChannelView({ attributes }: { attributes: BlockAttributes }) {
  const {
    channel_id: channelId,
    resource_maximum: resourceMaximum = 1,
    layout,
    featured,
  } = attributes

  const { data: apiResponse, isSuccess, isError } = useChannel(channelId)
  const included = apiResponse?.included || []
  const heroResource = findIncluded<HeroAPIRecord>(included, "HeroResource")
  const showHero = layout === "single" || (layout === "gallery" && featured)
  const episodeList = showHero
    ? heroResource?.attributes?.on_demand_resources
        .filter((item: HeroEpisodeAPIRecord) => item.id !== heroResource.id)
        .slice(0, resourceMaximum)
    : heroResource?.attributes?.on_demand_resources.slice(0, resourceMaximum)

  if (isError) {
    return <ErrorState />
  }

  if (isSuccess && heroResource) {
    return (
      <div className="d-f fd-c g-3">
        {showHero && <ChannelHero heroResource={heroResource} />}
        {layout === "gallery" &&
          heroResource.attributes.on_demand_resources &&
          episodeList && <EpisodeList episodes={episodeList} />}
      </div>
    )
  }

  return null
}

function EpisodeList({
  episodes,
}: {
  episodes: (EpisodeAPIRecord | HeroEpisodeAPIRecord)[]
}) {
  return (
    <CardList
      items={episodes}
      getItemProps={(item) => {
        const timeValue =
          item.attributes.time || item.attributes.published_to_library_at
        return {
          heading: item.attributes.title,
          poster:
            item.attributes.library_art_url ||
            item.attributes.art?.attributes?.variants?.medium ||
            "",
          description: date(timeValue, {
            style: "medium",
            year: true,
          }),
          link: item.attributes.church_center_url || item.attributes.url,
        }
      }}
    />
  )
}

function SeriesGalleryView({ attributes }: { attributes: BlockAttributes }) {
  const { channel_id: channelId, resource_maximum: resourceMaximum } =
    attributes

  const {
    data: apiResponse,
    isSuccess,
    isError,
  } = useSeries({ channelId, resourceMaximum })

  if (!isSuccess) {
    return <LoadingState />
  }

  if (isError) {
    return <ErrorState />
  }

  const { data: items } = apiResponse

  if (items.length > 0) {
    return (
      <CardList
        items={items}
        getItemProps={(item) => ({
          heading: item.attributes.title,
          poster: item.attributes.art?.attributes?.variants?.medium || "",
          description: date(
            item.attributes.started_at,
            item.attributes.ended_at,
            // @ts-expect-error: three args are allowed
            {
              style: "medium",
              year: true,
            },
          ),
          link: item.attributes.church_center_url,
        })}
      />
    )
  }

  return null
}

function SingleSeriesView({ attributes }: { attributes: BlockAttributes }) {
  const { channel_id: channelId, resource_id: resourceId, display } = attributes

  const {
    data: apiResponse = {},
    isSuccess,
    isError,
  } = useSeries({
    channelId,
    seriesId: display === "specific_resource" ? resourceId : undefined,
    resourceMaximum: 1,
  })

  const { data } = apiResponse
  const item = data?.[0] || data

  if (isError) {
    return <ErrorState />
  }

  if (!isSuccess) {
    return <LoadingState />
  }

  if (Array.isArray(item) && item.length === 0) {
    return <ErrorState />
  }

  if (item) {
    return (
      <FeaturedItem
        title={item.attributes.title}
        description={item.attributes.description}
        imageUrl={item.attributes.art.attributes.variants.medium}
        dateValue={[item.attributes.started_at, item.attributes.ended_at]}
        url={item.attributes.church_center_url}
      />
    )
  }

  return null
}

function CardList({
  items,
  getItemProps,
}: {
  items: BaseAPIRecord[]
  getItemProps: (item: (typeof items)[0]) => CardProps
}) {
  return (
    <div className="d-f fd-c ai-fs g-2">
      <CardGrid>
        {items.map((item) => (
          <Card key={item.id} {...getItemProps(item)} />
        ))}
      </CardGrid>
    </div>
  )
}

function FeaturedItem({
  title,
  description,
  imageUrl,
  dateValue,
  showWatchButton,
  showListenButton,
  showNotesButton,
  url,
}: {
  title: string
  description?: string
  imageUrl: string
  dateValue: string | string[]
  showWatchButton?: boolean | undefined
  showListenButton?: boolean | undefined
  showNotesButton?: boolean | undefined
  url: string
}) {
  const dateText = Array.isArray(dateValue)
    ? // @ts-expect-error: three args are allowed
      date(dateValue[0], dateValue[1], { style: "medium", year: true })
    : date(dateValue, { style: "medium", year: true })
  return (
    <div
      className="d-f fd-c fd-r@sm jc-fs ai-fs g-3"
      css={{
        img: {
          borderRadius: 8,
        },
      }}
    >
      <div className="f-1@sm">
        <a href={url}>
          <Image src={imageUrl} alt={title} aspectRatio="16/9" />
        </a>
      </div>
      <div className="f-1@sm d-f fd-c g-1 c-tint1">
        <LegacyHeading level={2} text={title} />
        <p className="fs-4 mb-0">{dateText}</p>
        {description && <p className="fs-3 mb-0">{description}</p>}
        <div className="d-f ai-c g-2">
          {showWatchButton && (
            <a href={url + "?media_intent=video"} className="btn fs-4">
              Watch
            </a>
          )}
          {showNotesButton && (
            <a
              href={`${url}/notes`}
              className={`btn fs-4 ${showWatchButton ? "secondary-btn" : ""}`}
            >
              Take notes
            </a>
          )}
          {showListenButton && (
            <a
              href={url + "?media_intent=audio"}
              className={`btn fs-4 ${showWatchButton || showNotesButton ? "secondary-btn" : ""}`}
            >
              Listen
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function ErrorState() {
  return (
    <BlankState.Root role="alert">
      <BlankState.Icon symbol="general#exclamation-triangle" />
      <BlankState.Content>
        <BlankState.Heading text="Error loading sermons" />
      </BlankState.Content>
    </BlankState.Root>
  )
}

function LoadingState() {
  return (
    <CardGrid>
      <Skeleton aspectRatio="308 / 242" />
      <Skeleton aspectRatio="308 / 242" />
      <Skeleton aspectRatio="308 / 242" />
      <Skeleton aspectRatio="308 / 242" />
    </CardGrid>
  )
}

const styles = {
  display: "flex",
  flexDirection: "column" as const,
  margin: "0 auto",
  maxWidth: "640px",
  marginBottom: "24px",
  gap: "12px",
}
